import React, { useEffect, useId, useRef, useState } from 'react';
import { Download, FileArchive, Loader2 } from 'lucide-react';
import Dialog from '../../common/Dialog';
import ExportService from '../../../services/admin/export.service';
import { describeApiError } from '../../../utils/apiErrors';
import type { ExportJob, ExportKind, ExportScope } from '../../../dtos/admin/export.dto';

export interface ExportDialogProps {
  open: boolean;
  scope: ExportScope;
  /** Required for scope 'patient' */
  patientId?: string;
  /** Pre-selected kind (the discharge flow opens it on 'transfer_packet') */
  initialKind?: ExportKind;
  onClose: () => void;
  /** Called when a job finishes (complete or failed), e.g. to refresh a history list */
  onFinished?: (job: ExportJob) => void;
}

const PURPOSE_MAX = 500;

/**
 * Start a data export (Phase 14, DAT-01..03): purpose/recipient note required, then the job runs
 * on the server and the dialog waits for it. The ZIP holds a PDF summary and CSV files; it can be
 * downloaded by the requester for 1 hour (patient) or 24 hours (agency).
 */
const ExportDialog: React.FC<ExportDialogProps> = ({ open, scope, patientId, initialKind = 'full', onClose, onFinished }) => {
  const baseId = useId();
  const [kind, setKind] = useState<ExportKind>(initialKind);
  const [purpose, setPurpose] = useState('');
  const [recipient, setRecipient] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [job, setJob] = useState<ExportJob | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [downloaded, setDownloaded] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    setKind(initialKind);
    setPurpose('');
    setRecipient('');
    setSubmitted(false);
    setJob(null);
    setBusy(false);
    setError(null);
    setDownloaded(null);
    return () => abortRef.current?.abort();
  }, [open, initialKind]);

  const purposeMissing = purpose.trim().length < 3;
  const waiting = job !== null && (job.status === 'queued' || job.status === 'running');

  const start = async (event: React.FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (purposeMissing || busy) return;
    setBusy(true);
    setError(null);
    try {
      const request = { purpose: purpose.trim(), recipient: recipient.trim() || undefined };
      const started = scope === 'agency'
        ? await ExportService.startAgencyExport(request)
        : await ExportService.startPatientExport(patientId ?? '', { ...request, kind });
      setJob(started);
      abortRef.current = new AbortController();
      const finished = await ExportService.waitForCompletion(started.exportId, { signal: abortRef.current.signal });
      setJob(finished);
      if (finished.status === 'failed') setError('The export could not be prepared. Try again, or contact support if it keeps failing.');
      onFinished?.(finished);
    } catch (err) {
      if (err instanceof Error && err.message === 'cancelled') return;
      setError(describeApiError(err, 'The export could not be started.'));
    } finally {
      setBusy(false);
    }
  };

  const download = async () => {
    if (!job) return;
    setError(null);
    try {
      setDownloaded(await ExportService.download(job.exportId));
    } catch (err) {
      setError(describeApiError(err, 'The download link could not be created.'));
    }
  };

  const titleId = `${baseId}-title`;
  const title = scope === 'agency' ? 'Export all agency data' : kind === 'transfer_packet' ? 'Patient record packet' : 'Export patient record';
  const windowText = scope === 'agency' ? '24 hours' : '1 hour';

  return (
    <Dialog open={open} onClose={() => !busy && onClose()} labelledBy={titleId}>
      <form onSubmit={start} className="text-left" noValidate>
        <h3 id={titleId} className="pr-6 text-lg font-semibold text-slate-800">{title}</h3>
        <p className="mt-1 text-sm text-slate-600">
          {scope === 'agency'
            ? 'Every patient record in your agency, as a ZIP with a PDF summary and CSV files per patient. Only an owner or admin can start this, after signing in within the last 5 minutes.'
            : 'The patient’s record as a ZIP with a PDF summary and CSV files. Documents are listed, not attached.'}
        </p>

        {!job && (
          <>
            {scope === 'patient' && (
              <fieldset className="mt-4">
                <legend className="text-xs font-medium text-slate-700">What to export</legend>
                <div className="mt-1 space-y-1 text-sm text-slate-800">
                  <label className="flex items-center gap-2">
                    <input type="radio" name={`${baseId}-kind`} checked={kind === 'full'} onChange={() => setKind('full')} />
                    Complete record (patient access request, legal or audit)
                  </label>
                  <label className="flex items-center gap-2">
                    <input type="radio" name={`${baseId}-kind`} checked={kind === 'transfer_packet'} onChange={() => setKind('transfer_packet')} />
                    Record packet for a receiving agency (chart, assessments, care plans, medications, authorizations, discharge summary)
                  </label>
                </div>
              </fieldset>
            )}

            <label className="mt-4 block" htmlFor={`${baseId}-purpose`}>
              <span className="block text-xs font-medium text-slate-700">Purpose of this export <span className="text-red-700">*</span></span>
              <textarea
                id={`${baseId}-purpose`}
                value={purpose}
                maxLength={PURPOSE_MAX}
                rows={3}
                onChange={(e) => setPurpose(e.target.value)}
                aria-invalid={submitted && purposeMissing}
                aria-describedby={`${baseId}-purpose-help`}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                placeholder="e.g. Patient access request from the parent; transfer to the receiving agency"
              />
              <span id={`${baseId}-purpose-help`} className="mt-1 block text-xs text-slate-500">Saved to the audit log as the reason for this disclosure. Don’t include clinical details.</span>
              {submitted && purposeMissing && <span className="mt-1 block text-xs text-red-700" role="alert">Enter the purpose (at least 3 characters).</span>}
            </label>

            <label className="mt-3 block" htmlFor={`${baseId}-recipient`}>
              <span className="block text-xs font-medium text-slate-700">Recipient (optional)</span>
              <input
                id={`${baseId}-recipient`}
                value={recipient}
                maxLength={200}
                onChange={(e) => setRecipient(e.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
                placeholder="Person or organization receiving the export"
              />
            </label>
          </>
        )}

        {waiting && (
          <p className="mt-4 flex items-center gap-2 text-sm text-slate-700" role="status">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Preparing the export. You can close this window; it will appear in the export history.
          </p>
        )}

        {job?.status === 'complete' && (
          <div className="mt-4 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900" role="status">
            <p className="flex items-center gap-2 font-medium"><FileArchive className="h-4 w-4" aria-hidden="true" /> The export is ready.</p>
            {job.countsLabel.length > 0 && <p className="mt-1 text-xs">{job.countsLabel.slice(0, 8).join(' · ')}</p>}
            <p className="mt-1 text-xs">Download within {windowText}. Each download is recorded in the audit log.</p>
            {downloaded && <p className="mt-1 break-all text-xs">Downloaded {downloaded}</p>}
          </div>
        )}

        {error && <p className="mt-3 text-sm text-red-700" role="alert">{error}</p>}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} disabled={busy && !waiting} className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm text-slate-800 hover:bg-slate-50 disabled:opacity-50">
            {job ? 'Close' : 'Cancel'}
          </button>
          {!job && (
            <button type="submit" disabled={busy} className="inline-flex items-center gap-2 rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700 disabled:opacity-50">
              <FileArchive className="h-4 w-4" aria-hidden="true" /> {busy ? 'Starting…' : 'Start export'}
            </button>
          )}
          {job?.status === 'complete' && job.canDownload && (
            <button type="button" onClick={download} className="inline-flex items-center gap-2 rounded-md bg-slate-800 px-4 py-2 text-sm font-medium text-white hover:bg-slate-700">
              <Download className="h-4 w-4" aria-hidden="true" /> Download ZIP
            </button>
          )}
        </div>
      </form>
    </Dialog>
  );
};

export default ExportDialog;
